import { useEffect, useMemo, useState } from 'react';
import cx from 'classnames';
import { Combobox } from '@globalvisioninc/ui/combobox';
import type { ComboboxOption, ComboboxOptionGroup } from '@globalvisioninc/ui/combobox';
import styles from './GVSearchSelect.module.css';

export interface GVSearchSelectOption {
  value: string;
  label: string;
  /** Optional row action (e.g. edit), rendered by Combobox at the end of the item */
  action?: ComboboxOption['action'];
}

export interface GVSearchSelectSection {
  label: string;
  options: GVSearchSelectOption[];
  /** When false, options in this section are never filtered by the search term. Defaults to true. */
  searchable?: boolean;
}

export interface GVSearchSelectProps {
  sections: GVSearchSelectSection[];
  value: string;
  onValueChange: (value: string) => void;
  /** Accessible label text. Always required; use `isLabelHidden` to hide it visually. */
  label: string;
  isLabelHidden?: boolean;
  searchPlaceholder?: string;
  disabled?: boolean;
  className?: string;
}

const toComboboxOption = (option: GVSearchSelectOption): ComboboxOption => ({
  label: option.label,
  value: option.value,
  action: option.action,
});

const GVSearchSelect = ({
  sections,
  value,
  onValueChange,
  label,
  isLabelHidden,
  searchPlaceholder = 'Search...',
  disabled,
  className,
}: GVSearchSelectProps) => {
  const selectedLabel = useMemo(() => {
    for (const section of sections) {
      const match = section.options.find((option) => option.value === value);
      if (match) return match.label;
    }
    return '';
  }, [sections, value]);

  const [searchText, setSearchText] = useState(selectedLabel);

  useEffect(() => {
    setSearchText(selectedLabel);
  }, [value, selectedLabel]);

  const isFilterSuppressed = selectedLabel !== '' && searchText === selectedLabel;

  const filteredOptions: ComboboxOptionGroup[] = useMemo(() => {
    const query = searchText.trim().toLowerCase();
    const groups: ComboboxOptionGroup[] = [];

    for (const section of sections) {
      const searchable = section.searchable !== false;
      const options =
        !query || isFilterSuppressed || !searchable
          ? section.options.map(toComboboxOption)
          : section.options.filter((option) => option.label.toLowerCase().includes(query)).map(toComboboxOption);

      if (options.length > 0) {
        groups.push({ label: section.label, options });
      }
    }

    return groups;
  }, [sections, searchText, isFilterSuppressed]);

  return (
    <Combobox.Root
      className={cx(styles.root, className)}
      options={filteredOptions}
      value={value}
      onValueChange={onValueChange}
      searchValue={searchText}
      onSearchChange={setSearchText}
      shouldFilter={false}
      isDisabled={disabled}
      isLabelHidden={isLabelHidden}
      placeholder={searchPlaceholder}
    >
      <Combobox.Label>{label}</Combobox.Label>
      <Combobox.Input />
    </Combobox.Root>
  );
};

export default GVSearchSelect;
